/**
 * Barre de contexte globale — WO-V5-A / US-029.1 (cible codex §4.2–4.3, adaptée aux API réelles).
 *
 * ORDRE DE LECTURE : organisation (lecture seule, issue du JWT) › perspective (Programmes |
 * Projets — pas d'« Équipes » : aucune API d'équipes au niveau organisation, dette nommée) ›
 * objet courant (sélecteur avec recherche, alimenté par `/programs` et `/workspaces`) › contexte
 * relié (programme parent du projet, ou projet courant vu depuis un programme).
 *
 * TROIS RÈGLES QUI NE SE NÉGOCIENT PAS
 *  - Le contexte vit dans l'URL (N4/N5) : `?programme=`, `?projet=`, `?perspective=`. À
 *    l'arrivée, l'URL l'emporte sur la mémoire locale ; quand l'URL ne dit rien, elle est
 *    complétée (replace) depuis l'état pour être partageable telle quelle.
 *  - Un changement de contexte n'est JAMAIS silencieux : si le projet courant sort du programme
 *    choisi, on demande de choisir un projet ou de rester — on ne sélectionne rien à sa place
 *    (ce que faisait `ContextSwitcher`, masqué quand ce composant est monté).
 *  - Les états sont honnêtes (§4.3) : chargement nommé, aucun programme, 0 / total, accès
 *    retiré, deep link périmé, erreur réseau = contexte « non vérifié » et sélection bloquée.
 *
 * UNE EXCEPTION À LA DEUXIÈME RÈGLE, ÉCRITE (N-37, décision consignée le 2026-10-12) : un lien profond vers
 * un projet accessible, autre que le projet actif, qui ne porte PAS `?programme=` désigne CE projet — le lien
 * est le choix : le programme du contexte le SUIT (celui du projet, ou aucun s'il n'en a pas) SANS question,
 * comme au sélecteur de projet, et l'adresse l'emporte sur le programme mémorisé (N4/N5). Sans elle,
 * `?projet=B` redevenait `?programme=<celui de A>&projet=B` et affichait « projet hors programme ». La
 * question reste posée dans les deux cas où le programme est un CHOIX : un programme ÉCRIT dans l'adresse, et
 * le programme choisi pour le projet déjà actif.
 *
 * Monté EN PERMANENCE par la coquille depuis le lot U6b (vague 1) : le drapeau `navContexte`
 * ne le gouverne plus, et le sélecteur historique de la barre latérale n'est plus rendu (une
 * surface, pas deux). Il porte aussi les accès directs aux niveaux (« Vue d'ensemble »,
 * « Vue du programme », « Plateforme » pour le super-administrateur) ; choisir un projet depuis
 * un écran d'un autre niveau ouvre la Vue projet.
 *
 * ADMINISTRATEUR DE LA PLATEFORME (ARB-SEC-01, décision du 2026-09-30) : il voit aussi, marqués
 * « non membre — rejoindre », les projets de l'organisation dont il n'est pas membre ; en choisir
 * un le fait REJOINDRE (membre propriétaire, action tracée) puis le sélectionne — jamais de
 * sélection sans appartenance. Les projets ARCHIVÉS ne sont pas proposés (ils restent consultables
 * dans Organisation › Projets), sauf le projet courant : le contexte ne disparaît jamais.
 */
import { useContext, useEffect, useRef, type JSX } from 'react'
import { Link, UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Layers, Boxes, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react'

import type { Session } from '@/auth/session'
import { useChangerContexte } from '@/hooks/useChangerContexte'
import { useNomOrganisation } from '@/hooks/useProfilOrganisation'
import { useProgram } from '@/hooks/useProgram'
import { useRejoindreProjet } from '@/hooks/useRejoindreProjet'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useT } from '@/i18n/language'
import {
  ecrireContexteUrl, lireContexteUrl, resoudreEtatSelecteur,
  type ContexteUrl, type EtatSelecteur, type ObjetAttendu, type Perspective,
} from '@/lib/contexteUrl'
import { SelecteurObjet } from '@/components/contexte/SelecteurObjet'
import { niveauDuChemin } from '@/lib/nav'

/** Clé de `hooks/useWorkspace.tsx` (mémoire locale de l'espace) — lue pour DÉTECTER un accès retiré. */
const CLE_ESPACE_MEMORISE = 'doit-active-workspace'

function espaceMemorise(): string | null {
  try { return localStorage.getItem(CLE_ESPACE_MEMORISE) } catch { return null }
}

/** Même règle que `estArchive` du fournisseur, lue ici sans en dépendre (les tests le simulent). */
const estArchive = (w: { status?: string | undefined }): boolean => w.status === 'archived'

/**
 * Adresse RÉELLE de l'historique du routeur (AUD-CRAWL-K6) : sous BrowserRouter, l'historique lit
 * `window.location` (pathname + search) ; sous MemoryRouter, sa pile en mémoire. Elle change dès
 * `history.replace`, AVANT le rendu de la transition qui mettra `useLocation` à jour. Un navigateur
 * sans `location` (routeur de données) rend `null` : seule l'adresse rendue est alors comparée.
 */
function adresseHistorique(historique: unknown): string | null {
  const l = (historique as { location?: { pathname?: unknown; search?: unknown } } | null)?.location
  return l && typeof l.pathname === 'string' && typeof l.search === 'string' ? l.pathname + l.search : null
}

const BOUTON_LIEN = 'inline-flex min-h-10 items-center rounded-md px-2 text-[0.8125rem] font-medium underline underline-offset-2 hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring'

/**
 * `ligne` (défaut) : la rangée complète sous la barre haute (tablette, feuille du téléphone).
 * `barre` (bureau, planche BarreHaute) : les deux sélecteurs « ◆ Programme ▾ › ● Projet ▾ » DANS la
 * barre haute ; les états qui exigent une décision (contexte non vérifié, lien périmé, accès retiré,
 * programme vide, projet hors programme) s'affichent dans un bandeau posé sous la barre, jamais
 * perdus. Une seule instance : les effets URL ↔ état ne tournent qu'une fois.
 */
export function BarreContexte({ session, variante = 'ligne' }: { session: Session; variante?: 'ligne' | 'barre' }): JSX.Element {
  const { t } = useT()
  const nomOrganisation = useNomOrganisation(session.organisationId)
  const { pathname, search, hash, state } = useLocation()
  const navigate = useNavigate()
  // Le navigateur du routeur EST son historique (BrowserRouter, MemoryRouter) : lu à l'échéance du
  // minuteur de l'écriture canonique, jamais au rendu (AUD-CRAWL-K6).
  const { navigator: historique } = useContext(UNSAFE_NavigationContext)
  const queryClient = useQueryClient()
  const programmes = useProgram()
  const espaces = useWorkspace()
  // HD-35 : seuls les espaces où le porteur est membre sont sélectionnables ; les autres restent
  // connus (noms) mais ne peuvent ni devenir courants, ni valider un lien profond.
  const accessibles = espaces.accessibles
  // Administrateur de la plateforme seul (vide sinon) : projets non membres, archivés exclus.
  const aRejoindre = espaces.aRejoindre ?? []
  const rejoindreProjet = useRejoindreProjet()
  // Proposés au choix : les projets actifs — un projet archivé n'est plus un lieu de travail —
  // plus le projet COURANT même archivé (le contexte ne disparaît jamais sous l'utilisateur).
  const proposables = accessibles.filter((w) => !estArchive(w) || w.workspace_id === espaces.activeId)

  const url = lireContexteUrl(search)
  const chargement = programmes.isLoading || espaces.isLoading
  const erreur = Boolean(programmes.error || espaces.error)
  const charge = !chargement && !erreur

  // Ce que l'utilisateur attendait : le lien profond d'abord, sinon la mémoire locale, lue UNE
  // fois au montage (le fournisseur la remplace silencieusement par le premier espace — c'est
  // cette substitution que l'on rend visible ici).
  const memoire = useRef<ObjetAttendu | null>(null)
  const memoireLue = useRef(false)
  if (!memoireLue.current) {
    memoireLue.current = true
    const m = espaceMemorise()
    if (m && !url.projet) memoire.current = { objet: 'projet', id: m, origine: 'stockage' }
  }
  // La mémoire locale garde la main tant que l'utilisateur n'a pas agi : la canonicalisation
  // de l'URL (ci-dessous) ne doit pas faire disparaître l'alerte « accès retiré » avant lui.
  const attendu: ObjetAttendu | null = memoire.current ?? (url.projet
    ? { objet: 'projet', id: url.projet, origine: 'url' }
    : url.programme
      ? { objet: 'programme', id: url.programme, origine: 'url' }
      : null)

  const etat: EtatSelecteur = resoudreEtatSelecteur({
    chargement, erreur, programmes: programmes.programs, espaces: accessibles,
    programmeCourant: programmes.activeId, projetCourant: espaces.activeId, attendu,
    perspective: url.perspective,
  })

  // FND-V050-185 : TOUTE mutation du contexte — ici comme depuis un écran (carte du tableau de
  // bord, création de projet, onboarding) — passe par `useChangerContexte` : fournisseurs ET URL.
  const { changerContexte } = useChangerContexte()
  const appliquer = (delta: Partial<ContexteUrl>, mode: 'push' | 'replace' = 'push', vers?: string): void => {
    memoire.current = null
    changerContexte(delta, { mode, vers })
  }

  // URL → état : l'URL n'est relue que lorsqu'ELLE change (arrivée, lien, retour arrière) ou
  // qu'une liste arrive pour valider un paramètre encore inconnu — JAMAIS parce que l'état a
  // changé. Un changement d'état est toujours accompagné de sa navigation (`useChangerContexte`),
  // mais le routeur la rend dans une transition : entre les deux, l'adresse affichée est
  // l'ANCIENNE, et la relire rétablissait le contexte que l'utilisateur venait de quitter
  // (FND-V050-160 : « programme=… » ré-ajouté ; FND-V050-185 : ancien projet rétabli).
  // État → URL quand l'URL est muette. Jamais l'inverse d'un paramètre PRÉSENT mais invalide : il
  // reste affiché comme deep link périmé jusqu'à ce que l'utilisateur agisse.
  const traitee = useRef<{ adresse: string; complete: boolean; listes: readonly unknown[] } | null>(null)
  const canonique = useRef<string | null>(null)
  // Adresse RÉELLEMENT courante (mise à jour à chaque rendu) : l'écriture canonique ci-dessous est
  // différée d'un tour et abandonnée si l'adresse a changé entre-temps. Mesuré le 2026-09-30 en
  // direct : sur un chargement de `/tree` (redirection `<Navigate>` vers `/projet?vue=hierarchie`),
  // cet effet et la redirection partaient du même rendu ; la navigation ci-dessous, construite sur
  // le chemin périmé `/tree`, arrivait APRÈS la redirection et la défaisait — page vide sur
  // `/tree?programme=…&projet=…` (idem /backlog, /graph, /sprints, /roadmap, /org, /plateforme).
  // AUD-CRAWL-K6 (crawl du 2026-10-09, `/plateforme` blanche 3 fois sur 6) : l'adresse lue AU RENDU ne
  // suffit pas — la redirection réécrit l'historique tout de suite mais n'est rendue que dans une
  // transition ; si le minuteur part avant ce rendu, cette adresse est encore l'alias. Le minuteur
  // compare donc AUSSI l'adresse réelle de l'historique (`adresseHistorique`).
  const adresseCourante = useRef(pathname + search)
  adresseCourante.current = pathname + search
  // `state` de l'adresse courante (lu à l'échéance du minuteur, comme l'adresse) : l'écriture canonique est
  // un `replace` qui COMPLÈTE l'adresse, elle n'en change pas la nature — AUD-CRAWL-P8 : sans lui, l'avis
  // « Cet écran a déménagé » (`location.state.demenage`) disparaissait dès qu'un projet était actif.
  const etatCourant = useRef<unknown>(state)
  etatCourant.current = state
  useEffect(() => () => { if (differee.current !== null) window.clearTimeout(differee.current) }, [])
  const differee = useRef<number | null>(null)
  useEffect(() => {
    if (!charge) return
    const adresse = pathname + search
    const listes = [accessibles, programmes.programs] as const
    const t0 = traitee.current
    const listesNeuves = !t0 || t0.listes.some((l, i) => l !== listes[i])
    if (t0 && t0.adresse === adresse && (t0.complete || !listesNeuves)) return
    const projetValide = Boolean(url.projet && accessibles.some((w) => w.workspace_id === url.projet))
    const programmeValide = Boolean(url.programme && programmes.programs.some((p) => p.program_id === url.programme))
    traitee.current = {
      adresse, listes,
      complete: (!url.projet || projetValide) && (!url.programme || programmeValide),
    }
    // Arriver sur un projet valide par l'adresse (lien, retour arrière) est un choix : l'alerte
    // tirée de la mémoire locale s'efface — sauf après notre propre canonicalisation.
    if (projetValide && adresse !== canonique.current) memoire.current = null
    if (projetValide && url.projet !== espaces.activeId) espaces.setActiveId(url.projet)
    if (programmeValide && url.programme !== programmes.activeId) programmes.setActiveId(url.programme)
    const complement: Partial<ContexteUrl> = {}
    if (!url.projet && espaces.activeId) complement.projet = espaces.activeId
    // Le programme du contexte est le programme CHOISI, sinon le parent du projet courant
    // (« contexte relié », §4.2) : l'URL partagée restitue les deux niveaux. Le projet courant est celui de
    // l'ADRESSE quand elle en désigne un d'accessible : `espaces.activeId` porte encore, dans ce passage, le
    // projet mémorisé (le `setActiveId` ci-dessus n'est lu qu'au rendu suivant) — son parent, écrit dans
    // l'adresse d'un autre projet, affichait « projet hors programme » (N-37).
    const projetDuContexte = projetValide ? url.projet : espaces.activeId
    const parent = espaces.workspaces.find((w) => w.workspace_id === projetDuContexte)?.program_id ?? null
    // Et le programme MÉMORISÉ ne s'y oppose pas, qu'il vienne de la barre (elle l'écrit dans l'adresse du projet
    // précédent, puis l'applique ci-dessus) ou du sélecteur ◆ : la mémoire n'en garde pas l'origine, et l'adresse
    // l'emporte sur elle (N4/N5). Un lien vers un AUTRE projet accessible, sans `?programme=`, désigne CE projet :
    // comme `choisirProjet`, le programme le suit, ou aucun — sans quoi `?projet=B` redevenait `?programme=<celui de
    // A>&projet=B` et « projet hors programme » (N-37). Restent des choix, et « projet hors programme » est alors
    // dit : un programme ÉCRIT dans l'adresse, et le programme choisi pour le projet déjà actif.
    const suitLeProjetDeLAdresse = projetValide && url.projet !== espaces.activeId && !url.programme
    if (suitLeProjetDeLAdresse && programmes.activeId !== parent) programmes.setActiveId(parent)
    const programmeContexte = suitLeProjetDeLAdresse ? parent : (programmes.activeId ?? parent)
    if (!url.programme && programmeContexte) complement.programme = programmeContexte
    if (Object.keys(complement).length > 0) {
      const suivant = ecrireContexteUrl(search, complement)
      if (suivant !== search) {
        canonique.current = pathname + suivant
        // Le fragment (#ancre) fait partie de l'adresse demandée : le perdre ici annulait le
        // défilement et le focus de la section visée (FND-V050-195, #release-environments).
        const attendue = pathname + search
        if (differee.current !== null) window.clearTimeout(differee.current)
        differee.current = window.setTimeout(() => {
          differee.current = null
          // Redirigée entre-temps (rendue, ou seulement écrite dans l'historique) : l'effet reprend sur la nouvelle.
          const reelle = adresseHistorique(historique)
          if (adresseCourante.current !== attendue || (reelle !== null && reelle !== attendue)) return
          navigate({ pathname, search: suivant, hash }, { replace: true, state: etatCourant.current })
        }, 0)
      }
    }
    // `espaces`/`programmes` sont des objets recréés à chaque rendu : on dépend de leurs champs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charge, search, pathname, hash, espaces.activeId, programmes.activeId, accessibles, programmes.programs])

  const projetCourant = espaces.workspaces.find((w) => w.workspace_id === espaces.activeId) ?? null
  const programmeCourant = programmes.programs.find((p) => p.program_id === programmes.activeId) ?? null
  const programmeDuProjet = projetCourant?.program_id
    ? programmes.programs.find((p) => p.program_id === projetCourant.program_id) ?? null
    : null
  // Le sélecteur de projet liste TOUS les projets ACCESSIBLES de l'organisation (le programme
  // parent en détail) : choisir un projet d'un autre programme rattache ce programme, c'est un
  // choix explicite. Le compteur « n / total » dit combien appartiennent au programme courant.
  const espacesDuProgramme = programmes.activeId
    ? accessibles.filter((w) => w.program_id === programmes.activeId)
    : accessibles
  const nomProgramme = (id: string | null | undefined): string | undefined =>
    id ? programmes.programs.find((p) => p.program_id === id)?.name : undefined

  const choisirPerspective = (p: Perspective): void => appliquer({ perspective: p })
  const choisirProjet = (id: string): void => {
    const w = espaces.workspaces.find((x) => x.workspace_id === id)
    // Même entrée si l'on est déjà au niveau Projet (AC-S1) ; sinon la Vue projet.
    const vers = niveauDuChemin(pathname, search) === 'projet' ? pathname : '/projet'
    appliquer({ projet: id, programme: w?.program_id ?? null }, 'push', vers)
  }
  // Un projet « à rejoindre » n'est sélectionné qu'APRÈS le rattachement et la relecture de la
  // liste : le sélectionner avant le ferait passer pour un lien périmé. Refus ⇒ rien ne change.
  const choisirOuRejoindre = (id: string): void => {
    const cible = aRejoindre.find((w) => w.workspace_id === id)
    if (!cible) { rejoindreProjet.effacer(); choisirProjet(id); return }
    void rejoindreProjet.rejoindre(id, cible.name).then((resultat) => { if (resultat) choisirProjet(id) })
  }
  const optionsARejoindre = aRejoindre.map((w) => ({
    id: w.workspace_id, label: w.name,
    detail: t('contexte.nonMembreRejoindre', 'non membre — rejoindre'),
  }))
  // Lien profond vers un projet de l'organisation que l'administrateur n'a pas encore rejoint
  // (vues de programme, portefeuille, lien partagé) : on lui propose de le rejoindre sur place.
  const projetARejoindreDuLien = etat.type === 'deep-link-perime' && etat.objet === 'projet'
    ? aRejoindre.find((w) => w.workspace_id === etat.id) ?? null
    : null
  const boutonRejoindreLien = projetARejoindreDuLien ? (
    <button type="button" data-testid="contexte-deep-link-rejoindre" className={BOUTON_LIEN}
            disabled={rejoindreProjet.enCours !== null}
            onClick={() => choisirOuRejoindre(projetARejoindreDuLien.workspace_id)}>
      {t('contexte.rejoindreCeProjet', 'Rejoindre « {{nom}} » en tant que propriétaire', { nom: projetARejoindreDuLien.name })}
    </button>
  ) : null
  const fermerRetour = (
    <button type="button" data-testid="contexte-rejoindre-fermer" className={BOUTON_LIEN} onClick={rejoindreProjet.effacer}>
      {t('contexte.fermer', 'Fermer')}
    </button>
  )
  const retourRejoindre = rejoindreProjet.erreur ? (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
      <span role="alert" data-testid="contexte-rejoindre-erreur" className="text-destructive">{rejoindreProjet.erreur}</span>
      {fermerRetour}
    </span>
  ) : rejoindreProjet.enCours ? (
    <span role="status" data-testid="contexte-rejoindre-en-cours" className="inline-flex min-h-10 items-center text-muted-foreground">
      {t('contexte.rejoindreEnCours', 'Rattachement au projet…')}
    </span>
  ) : rejoindreProjet.message ? (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
      <span role="status" data-testid="contexte-rejoindre-succes">{rejoindreProjet.message}</span>
      {fermerRetour}
    </span>
  ) : null
  const motifProjetBloque = etat.type === 'non-verifie'
    ? t('contexte.selectionBloquee', 'Sélection bloquée : contexte non vérifié')
    : t('contexte.rejoindreEnCours', 'Rattachement au projet…')
  const choisirProgramme = (id: string): void => appliquer({ programme: id })
  const reessayer = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
    void queryClient.invalidateQueries({ queryKey: ['programs'] })
  }

  const nonVerifie = etat.type === 'non-verifie'

  if (variante === 'barre') {
    // FND-V050-160 : TOUS les projets accessibles (règle des deux niveaux, planche BarreHaute),
    // ceux du programme courant d'abord ; le programme parent en détail, « Sans programme » sinon.
    // Choisir un projet d'un autre programme — ou sans programme — change le programme du contexte.
    const programmeTri = programmes.activeId ?? projetCourant?.program_id ?? null
    const optionsProjetsBarre = [
      ...proposables.filter((w) => programmeTri && w.program_id === programmeTri),
      ...proposables.filter((w) => !(programmeTri && w.program_id === programmeTri)),
    ].map((w) => ({
      id: w.workspace_id, label: w.name,
      detail: nomProgramme(w.program_id) ?? t('contexte.sansProgramme', 'Sans programme'),
    })).concat(optionsARejoindre)
    const programmeAffiche = programmeDuProjet?.program_id ?? programmes.activeId
    const blocage = ['non-verifie', 'deep-link-perime', 'acces-retire', 'programme-vide', 'projet-hors-programme'].includes(etat.type)
    return (
      <section aria-label={t('contexte.region', 'Contexte de travail')} data-testid="barre-contexte" data-variante="barre"
               data-contexte-verifie={erreur ? 'false' : 'true'}
               className="flex min-w-0 items-center gap-1.5 text-[0.8125rem]">
        {etat.type === 'chargement' ? (
          <div role="status" data-testid="contexte-etat-chargement" className="inline-flex min-h-11 items-center gap-2 text-muted-foreground">
            <span className="h-4 w-32 animate-pulse rounded bg-muted" aria-hidden="true" />
            <span className="sr-only">{t('contexte.chargement', 'Chargement du contexte…')}</span>
          </div>
        ) : etat.type === 'aucun-projet' && aRejoindre.length === 0 ? (
          <span data-testid="contexte-etat-aucun-projet" className="inline-flex min-h-11 items-center text-muted-foreground">
            {espaces.workspaces.length > 0
              ? t('contexte.aucunEspaceAttribue', 'Aucun espace ne vous est attribué — demandez une invitation.')
              : t('contexte.aucunProjet', 'Aucun projet accessible dans cette organisation')}
          </span>
        ) : (
          <>
            {programmes.programs.length > 0 ? (
              <>
                <SelecteurObjet forme="puce" symbole="◆" testid="contexte-programme"
                  options={programmes.programs.map((p) => ({ id: p.program_id, label: p.name }))}
                  courant={programmeAffiche} libelle={t('contexte.choisirProgramme', 'Choisir un programme')}
                  placeholder={t('contexte.rechercherProgramme', 'Rechercher un programme')}
                  onChoisir={choisirProgramme} desactive={nonVerifie}
                  motifDesactive={t('contexte.selectionBloquee', 'Sélection bloquée : contexte non vérifié')} />
                <span aria-hidden="true" className="text-muted-foreground">›</span>
              </>
            ) : null}
            <SelecteurObjet forme="puce" symbole="●"
              options={optionsProjetsBarre}
              courant={espaces.activeId} libelle={t('contexte.choisirProjet', 'Choisir un projet')}
              placeholder={t('contexte.rechercherProjet', 'Rechercher un projet')}
              onChoisir={choisirOuRejoindre} desactive={nonVerifie || rejoindreProjet.enCours !== null}
              motifDesactive={motifProjetBloque} />
          </>
        )}
        {session.roles.includes('admin') ? (
          <Link to="/plateforme/sante" data-testid="contexte-plateforme"
                className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-md border px-2 text-[0.8125rem] font-medium hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
            <ShieldCheck className="size-3.5 shrink-0 text-brand-text" aria-hidden="true" />
            {t('contexte.plateforme', 'Plateforme')}
          </Link>
        ) : null}
        {retourRejoindre && !blocage ? (
          <div data-testid="contexte-bandeau-rejoindre"
               className="absolute inset-x-0 top-full flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-card px-4 py-1 text-[0.8125rem]">
            {retourRejoindre}
          </div>
        ) : null}
        {blocage ? (
          <div data-testid="contexte-bandeau-etats"
               className="absolute inset-x-0 top-full flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-warning-soft px-4 py-1 text-[0.8125rem]">
        {/* États qui exigent une décision ou une reprise — toujours nommés, jamais un vide. */}
        {retourRejoindre}
        {etat.type === 'non-verifie' ? (
          <span role="status" data-testid="contexte-etat-non-verifie" className="inline-flex min-h-10 items-center gap-1.5 text-destructive">
            <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
            <span>{t('contexte.nonVerifie', 'Contexte non vérifié : réseau indisponible, changement de contexte bloqué')}</span>
            <button type="button" data-testid="contexte-reessayer" onClick={reessayer}
                    className="inline-flex min-h-10 items-center gap-1 rounded-md border px-2 text-[0.8125rem] font-medium hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
              <RefreshCw className="size-3.5" aria-hidden="true" />
              {t('contexte.reessayer', 'Réessayer')}
            </button>
          </span>
        ) : null}
  
        {etat.type === 'deep-link-perime' ? (
          <div role="status" data-testid="contexte-etat-deep-link-perime" className="flex min-w-0 flex-wrap items-center gap-2 text-destructive">
            <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
            <span>
              {etat.objet === 'projet'
                ? t('contexte.deepLinkPerimeProjet', 'Le projet « {{id}} » est introuvable ou inaccessible.', { id: etat.id })
                : t('contexte.deepLinkPerimeProgramme', 'Le programme « {{id}} » est introuvable ou inaccessible.', { id: etat.id })}
            </span>
            <button type="button" className={BOUTON_LIEN}
                    onClick={() => appliquer(etat.objet === 'projet' ? { projet: espaces.activeId } : { programme: null }, 'replace')}>
              {t('contexte.revenirParent', 'Revenir au contexte accessible')}
            </button>
            {boutonRejoindreLien}
          </div>
        ) : null}
  
        {etat.type === 'acces-retire' ? (
          <div role="alert" data-testid="contexte-etat-acces-retire" className="flex min-w-0 flex-wrap items-center gap-2 text-destructive">
            <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
            <span>
              {t('contexte.accesRetire', 'Votre accès au projet « {{id}} » a été retiré, ou il a été supprimé. Vous êtes maintenant dans « {{actuel}} ».',
                { id: etat.id, actuel: projetCourant?.name ?? '—' })}
            </span>
            <button type="button" className={BOUTON_LIEN} onClick={() => { memoire.current = null; appliquer({ projet: espaces.activeId }, 'replace') }}>
              {t('contexte.continuerIci', 'Continuer ici')}
            </button>
          </div>
        ) : null}
  
        {etat.type === 'programme-vide' ? (
          <div data-testid="contexte-etat-programme-vide" className="flex min-w-0 flex-wrap items-center gap-2 text-muted-foreground">
            <span>
              {t('contexte.programmeVide', 'Aucun projet rattaché à ce programme : {{n}} / {{total}}', { n: 0, total: etat.total })}
            </span>
            <button type="button" data-testid="contexte-tous" className={BOUTON_LIEN} onClick={() => appliquer({ programme: null })}>
              {t('contexte.voirTousProjets', 'Voir tous les projets')}
            </button>
          </div>
        ) : null}
  
        {etat.type === 'projet-hors-programme' ? (
          <div role="group" aria-labelledby="contexte-hors-programme-titre" data-testid="contexte-etat-projet-hors-programme"
               className="flex w-full min-w-0 flex-wrap items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1">
            <span id="contexte-hors-programme-titre">
              {t('contexte.projetHorsProgramme', 'Le projet « {{projet}} » n’est pas dans le programme « {{programme}} ». Choisissez un projet du programme, ou restez dans le contexte précédent.',
                { projet: projetCourant?.name ?? etat.projet, programme: programmeCourant?.name ?? etat.programme })}
            </span>
            {etat.candidats.map((id) => {
              const w = espaces.workspaces.find((x) => x.workspace_id === id)
              return (
                <button key={id} type="button" data-testid={`contexte-choisir-${id}`} className={BOUTON_LIEN}
                        onClick={() => appliquer({ projet: id, programme: etat.programme })}>
                  {w?.name ?? id}
                </button>
              )
            })}
            <button type="button" data-testid="contexte-rester" className={BOUTON_LIEN}
                    onClick={() => appliquer({ programme: projetCourant?.program_id ?? null })}>
              {t('contexte.rester', 'Rester dans « {{projet}} »', { projet: projetCourant?.name ?? etat.projet })}
            </button>
          </div>
        ) : null}
          </div>
        ) : null}
      </section>
    )
  }

  return (
    <section aria-label={t('contexte.region', 'Contexte de travail')} data-testid="barre-contexte"
             data-contexte-verifie={erreur ? 'false' : 'true'}
             className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted/30 px-2 py-1 text-[0.8125rem] sm:px-3">
      {/* 1. Organisation — issue du jeton : la changer change le jeton, pas l'URL. Lot ORG : son NOM
          (pas son identifiant technique) mène à sa fiche « Mon organisation ». */}
      {session.organisationId ? (
        <Link to="/org/fiche" data-testid="contexte-organisation" title={session.organisationId}
              className="inline-flex min-h-10 min-w-0 items-center gap-1.5 rounded-md px-1 text-muted-foreground underline-offset-2 hover:bg-accent hover:underline focus-visible:ring-2 focus-visible:ring-ring">
          <Building2 className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="sr-only">{t('contexte.organisation', 'Organisation')} :</span>
          <span className="truncate font-medium">{nomOrganisation ?? session.organisationId}</span>
        </Link>
      ) : (
        <span data-testid="contexte-organisation" className="inline-flex min-h-10 min-w-0 items-center gap-1.5 text-muted-foreground">
          <Building2 className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="sr-only">{t('contexte.organisation', 'Organisation')} :</span>
          <span className="technical-id truncate">{t('contexte.organisationInconnue', 'organisation inconnue')}</span>
        </span>
      )}
      <span aria-hidden="true" className="text-muted-foreground/50">›</span>

      {/* 2. Perspective — dans l'URL (N4). */}
      <div role="radiogroup" aria-label={t('contexte.perspective', 'Perspective')} data-testid="contexte-perspective"
           className="inline-flex rounded-md border bg-background p-0.5">
        {(['programmes', 'projets'] as const).map((p) => (
          <button key={p} type="button" role="radio" aria-checked={url.perspective === p}
                  data-testid={`contexte-perspective-${p}`}
                  onClick={() => choisirPerspective(p)}
                  className={`min-h-9 rounded px-2 text-[0.8125rem] font-medium focus-visible:ring-2 focus-visible:ring-ring ${
                    url.perspective === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}>
            {p === 'programmes' ? t('contexte.perspectiveProgrammes', 'Programmes') : t('contexte.perspectiveProjets', 'Projets')}
          </button>
        ))}
      </div>
      <span aria-hidden="true" className="text-muted-foreground/50">›</span>

      {/* 3. Objet courant + 4. contexte relié — ou l'état qui empêche de les afficher. */}
      {etat.type === 'chargement' ? (
        <div role="status" data-testid="contexte-etat-chargement" className="inline-flex min-h-10 items-center gap-2 text-muted-foreground">
          <span className="h-4 w-40 animate-pulse rounded bg-muted" aria-hidden="true" />
          <span>{t('contexte.chargement', 'Chargement du contexte…')}</span>
        </div>
      ) : etat.type === 'aucun-projet' && aRejoindre.length === 0 ? (
        espaces.workspaces.length > 0 ? (
          // Des projets existent mais aucun n'est attribué au porteur (HD-35) : le dire, ne pas
          // emprunter le premier de la liste.
          <span data-testid="contexte-etat-aucun-espace-attribue" className="inline-flex min-h-10 items-center text-muted-foreground">
            {t('contexte.aucunEspaceAttribue', 'Aucun espace ne vous est attribué — demandez une invitation.')}
          </span>
        ) : (
          <span data-testid="contexte-etat-aucun-projet" className="inline-flex min-h-10 items-center text-muted-foreground">
            {t('contexte.aucunProjet', 'Aucun projet accessible dans cette organisation')}
          </span>
        )
      ) : (
        <>
          {url.perspective === 'programmes' ? (
            etat.type === 'aucun-programme' ? (
              <span data-testid="contexte-etat-aucun-programme" className="inline-flex min-h-10 items-center text-muted-foreground">
                {t('contexte.aucunProgramme', 'Aucun programme : un projet peut exister sans programme.')}
              </span>
            ) : (
              <SelecteurObjet
                options={programmes.programs.map((p) => ({ id: p.program_id, label: p.name }))}
                courant={programmes.activeId} libelle={t('contexte.choisirProgramme', 'Choisir un programme')}
                placeholder={t('contexte.rechercherProgramme', 'Rechercher un programme')}
                onChoisir={choisirProgramme} desactive={nonVerifie}
                motifDesactive={t('contexte.selectionBloquee', 'Sélection bloquée : contexte non vérifié')} />
            )
          ) : (
            <SelecteurObjet
              options={proposables.map((w) => {
                const detail = nomProgramme(w.program_id)
                return detail ? { id: w.workspace_id, label: w.name, detail } : { id: w.workspace_id, label: w.name }
              }).concat(optionsARejoindre)}
              courant={espaces.activeId} libelle={t('contexte.choisirProjet', 'Choisir un projet')}
              placeholder={t('contexte.rechercherProjet', 'Rechercher un projet')}
              onChoisir={choisirOuRejoindre} desactive={nonVerifie || rejoindreProjet.enCours !== null}
              motifDesactive={motifProjetBloque} />
          )}

          {/* Compteur du programme courant : « n / total » — le filtre se VOIT. */}
          {url.perspective === 'projets' && programmes.activeId && etat.type !== 'programme-vide' ? (
            <span data-testid="contexte-compteur" className="inline-flex min-h-10 items-center text-xs text-muted-foreground"
                  title={t('ctx.filterTitle', '{{n}} projets sur {{total}} rattachés à ce programme', { n: espacesDuProgramme.length, total: accessibles.length })}>
              {espacesDuProgramme.length} / {accessibles.length}
            </span>
          ) : null}

          {/* 4. Contexte relié. */}
          <span data-testid="contexte-relie" className="inline-flex min-h-10 min-w-0 items-center gap-1.5 text-muted-foreground">
            {url.perspective === 'projets' ? (
              <>
                <Layers className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">{t('contexte.programmeParent', 'Programme parent')} :</span>
                <span className="truncate">{programmeDuProjet?.name ?? t('contexte.sansProgramme', 'Sans programme')}</span>
              </>
            ) : (
              <>
                <Boxes className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">{t('contexte.projetCourant', 'Projet courant')} :</span>
                <span className="truncate">{projetCourant?.name ?? t('contexte.aucunProjetChoisi', 'Aucun projet choisi')}</span>
              </>
            )}
          </span>
        </>
      )}

      {retourRejoindre}

      {/* Accès directs aux niveaux : des NAVIGATIONS, distinctes des sélecteurs (A11Y-02). */}
      <span className="ml-auto inline-flex flex-wrap items-center gap-x-1" data-testid="contexte-niveaux">
        <Link to="/tableau-de-bord" data-testid="contexte-vue-ensemble" className={BOUTON_LIEN}>
          {t('contexte.vueEnsemble', 'Vue d’ensemble')}
        </Link>
        {projetCourant ? (
          <Link to="/projet" data-testid="contexte-vue-projet" className={BOUTON_LIEN}>
            {t('contexte.vueProjet', 'Vue projet')}
          </Link>
        ) : null}
        {programmes.activeId || programmeDuProjet ? (
          <Link to="/programme" data-testid="contexte-vue-programme" className={BOUTON_LIEN}>
            {t('contexte.vueProgramme', 'Vue du programme')}
          </Link>
        ) : null}
        {session.roles.includes('admin') ? (
          <Link to="/plateforme/sante" data-testid="contexte-plateforme" className={BOUTON_LIEN}>
            {t('contexte.plateforme', 'Plateforme')}
          </Link>
        ) : null}
      </span>

      {/* États qui exigent une décision ou une reprise — toujours nommés, jamais un vide. */}
      {etat.type === 'non-verifie' ? (
        <span role="status" data-testid="contexte-etat-non-verifie" className="inline-flex min-h-10 items-center gap-1.5 text-destructive">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>{t('contexte.nonVerifie', 'Contexte non vérifié : réseau indisponible, changement de contexte bloqué')}</span>
          <button type="button" data-testid="contexte-reessayer" onClick={reessayer}
                  className="inline-flex min-h-10 items-center gap-1 rounded-md border px-2 text-[0.8125rem] font-medium hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
            <RefreshCw className="size-3.5" aria-hidden="true" />
            {t('contexte.reessayer', 'Réessayer')}
          </button>
        </span>
      ) : null}

      {etat.type === 'deep-link-perime' ? (
        <div role="status" data-testid="contexte-etat-deep-link-perime" className="flex min-w-0 flex-wrap items-center gap-2 text-destructive">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>
            {etat.objet === 'projet'
              ? t('contexte.deepLinkPerimeProjet', 'Le projet « {{id}} » est introuvable ou inaccessible.', { id: etat.id })
              : t('contexte.deepLinkPerimeProgramme', 'Le programme « {{id}} » est introuvable ou inaccessible.', { id: etat.id })}
          </span>
          <button type="button" className={BOUTON_LIEN}
                  onClick={() => appliquer(etat.objet === 'projet' ? { projet: espaces.activeId } : { programme: null }, 'replace')}>
            {t('contexte.revenirParent', 'Revenir au contexte accessible')}
          </button>
          {boutonRejoindreLien}
        </div>
      ) : null}

      {etat.type === 'acces-retire' ? (
        <div role="alert" data-testid="contexte-etat-acces-retire" className="flex min-w-0 flex-wrap items-center gap-2 text-destructive">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>
            {t('contexte.accesRetire', 'Votre accès au projet « {{id}} » a été retiré, ou il a été supprimé. Vous êtes maintenant dans « {{actuel}} ».',
              { id: etat.id, actuel: projetCourant?.name ?? '—' })}
          </span>
          <button type="button" className={BOUTON_LIEN} onClick={() => { memoire.current = null; appliquer({ projet: espaces.activeId }, 'replace') }}>
            {t('contexte.continuerIci', 'Continuer ici')}
          </button>
        </div>
      ) : null}

      {etat.type === 'programme-vide' ? (
        <div data-testid="contexte-etat-programme-vide" className="flex min-w-0 flex-wrap items-center gap-2 text-muted-foreground">
          <span>
            {t('contexte.programmeVide', 'Aucun projet rattaché à ce programme : {{n}} / {{total}}', { n: 0, total: etat.total })}
          </span>
          <button type="button" data-testid="contexte-tous" className={BOUTON_LIEN} onClick={() => appliquer({ programme: null })}>
            {t('contexte.voirTousProjets', 'Voir tous les projets')}
          </button>
        </div>
      ) : null}

      {etat.type === 'projet-hors-programme' ? (
        <div role="group" aria-labelledby="contexte-hors-programme-titre" data-testid="contexte-etat-projet-hors-programme"
             className="flex w-full min-w-0 flex-wrap items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1">
          <span id="contexte-hors-programme-titre">
            {t('contexte.projetHorsProgramme', 'Le projet « {{projet}} » n’est pas dans le programme « {{programme}} ». Choisissez un projet du programme, ou restez dans le contexte précédent.',
              { projet: projetCourant?.name ?? etat.projet, programme: programmeCourant?.name ?? etat.programme })}
          </span>
          {etat.candidats.map((id) => {
            const w = espaces.workspaces.find((x) => x.workspace_id === id)
            return (
              <button key={id} type="button" data-testid={`contexte-choisir-${id}`} className={BOUTON_LIEN}
                      onClick={() => appliquer({ projet: id, programme: etat.programme })}>
                {w?.name ?? id}
              </button>
            )
          })}
          <button type="button" data-testid="contexte-rester" className={BOUTON_LIEN}
                  onClick={() => appliquer({ programme: projetCourant?.program_id ?? null })}>
            {t('contexte.rester', 'Rester dans « {{projet}} »', { projet: projetCourant?.name ?? etat.projet })}
          </button>
        </div>
      ) : null}
    </section>
  )
}
